// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterSubscription.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from "react";
import { observer } from "mobx-react";
import OperatorValue from "modules/ReportDesigner/Fields/Filter/Izenda.OperatorValue";
import OperatorGroup from "modules/ReportDesigner/Fields/Filter/Izenda.OperatorGroup";
import GridLayout from "share/components/controls/Izenda.GridLayout";
import CONSTANTS from "logic/data/Izenda.FilterDetailsConfigConstants";
import { any, findIndex } from "lodash/fp";

@observer
class FilterSubscription extends Component {
  static displayName = "FilterSubscription";

  static propTypes = {
    filters: PropTypes.object
  };

  constructor(props) {
    super(props);
    if (!!props.dashboardDetail) {
      this.originalGetData = props.filterStore.getData;
      props.filterStore.getData = this.customGetData();
    }
  }

  /**
   * Handle change of operator value.
   * @param {object} item
   * @param {number} index
   */
  onChangeOperatorValue = (item: any, index: number) => {
    return value => {
      const isDashboardDetail = !!this.props.dashboardDetail;
      let { filters, data, updateCascadingFilterValue,filterStore } = this.props;
      const filterFieldId = isDashboardDetail
        ? item.fieldDetails.id
        : (filters.visibleConfigFields[index] && filters.visibleConfigFields[index].id) ||
          item.fieldDetails.id;

      item.updateFilterValue(data, filterFieldId, isDashboardDetail, value, item.selectedOperatorId, item.fieldDetails.operatorSetting);
      updateCascadingFilterValue &&
        updateCascadingFilterValue(
          item,
          filters.visibleConfigFields,
          isDashboardDetail
            ? data.subscriptionCommonFilterFields
            : data.subscriptionFilterFields
        );
        filterStore && filterStore.updateReportDesignerCascadingFilter(item.fieldDetails);
    };
  };

  customGetData = () => {
    const {
      dashboardDetail,
      data: { subscriptionCommonFilterFields, subscriptionFilterFields },
      filterStore
    } = this.props;
    const filterFields = !!dashboardDetail
      ? subscriptionCommonFilterFields
      : subscriptionFilterFields;
    const originalGetData = this.originalGetData;
    return (pageInfo, configField, searchKey, isSearching) => {
      return originalGetData.call(
        filterStore,
        pageInfo,
        configField,
        searchKey,
        isSearching,
        filterFields
      );
    };
  };

  /**
   * Render operator value component.
   */
  renderOperatorValue = (item: any, index: number, reportFilter: any) => {
    let props = this.props;
    const isDashboardDetail = !!props.dashboardDetail;
    const filterFieldId = isDashboardDetail
      ? item.fieldDetails.id
      : (reportFilter.filterFields[index] &&
          reportFilter.filterFields[index].id) ||
        item.fieldDetails.id;
    let filterInfo = item.filterInfo;

    if (this.doesRenderOperator(filterInfo.group.name, filterInfo.operator.name)) {
      return null;
    }

    return (
      <OperatorValue
        filterSource={item.operatorSourceValue}
        filterInfo={item.filterInfo}
        operatorGroup={item}
        viewMode
        isLazyLoading={props.isLazyLoading}
        store={props.filterStore}
        configField={item}
        onChange={this.onChangeOperatorValue(item, index)}
      />
    );
  };

  updateSourceValue = (item: any, index: number, reportFilter: any) => {
    const isDashboardDetail = !!this.props.dashboardDetail;
    const filterFieldId = isDashboardDetail
      ? item.fieldDetails.id
      : (reportFilter.filterFields[index] &&
          reportFilter.filterFields[index].id) ||
        item.fieldDetails.id;

    return (...args) =>
      item.setOperatorIdSubscription.call(
        item,
        this.props.data,
        filterFieldId,
        isDashboardDetail,
        ...args
      );
  };

  /**
   * Render operator component.
   */
  renderOperator = (item: any, index: number, reportFilter: any) => {
    let filterInfo = item.filterInfo;

    if (
      !this.doesRenderOperator(filterInfo.group.name, filterInfo.operator.name)
    )
      return null;

    return (
      <OperatorGroup
        notInReportDesignerMode
        configField={item}
        updateSourceValue={this.updateSourceValue(item, index, reportFilter)}
      />
    );
  };

  /**
   * Check need to render operator.
   * @param {string} operatorGroupName
   * @param {string} operatorName
   */
  doesRenderOperator(operatorGroupName: string, operatorName: string) {
    return (
      operatorGroupName === CONSTANTS.OPERATOR_GROUP_KEYS.BOOLEAN ||
      any(value => operatorName === value, CONSTANTS.OPERATOR_KEYS)
    );
  }

  buildHintMessage(item: any) {
    let message = null;
    const fieldDetails = item.fieldDetails;
    const operatorKey = item.filterInfo.operator.key;
    const keys = [
      "equals(manualentry)",
      "equals(manualentrynoauto-complete)",
      "notequal(manualentry)",
      "notequal(manualentrynoauto-complete)",
      "equals(selection)",
      "notequal(selection)"
    ];

    const index = findIndex(function(item) {
      return item === operatorKey;
    }, keys);

    if (index === -1) return message;

    if (fieldDetails.operatorSetting === CONSTANTS.SELECT_SETTINGS.MULTIPLE) {
      message = CONSTANTS.SELECT_TEXT.MULTIPLE;
    } else {
      message = CONSTANTS.SELECT_TEXT.SINGLE;
    }

    return message;
  }

  /**
   * Render Filter Subscription component
   * @returns {object} component
   */
  renderFilterSubscription = () => {
    let reportFilter = this.props.filters;
    return reportFilter.visibleConfigFields.map((item: any, index: number) => {
      let fieldDetails = item.fieldDetails;
      let filterLabel = fieldDetails.alias;
      const hintMessage = this.buildHintMessage(item);

      return (
        <GridLayout.Row key={index}>
          <GridLayout.Col cols={3} className="control-label">
            {index + 1}.{filterLabel}
          </GridLayout.Col>
          <GridLayout.Col cols={3} className="control-label">
            {item.filterInfo.operator.label}
          </GridLayout.Col>
          <GridLayout.Col cols={5}>
            {this.renderOperator(item, index, reportFilter)}
            {this.renderOperatorValue(item, index, reportFilter)}
          </GridLayout.Col>
          <GridLayout.Col cols={1}>
            {hintMessage &&
              <span
                className="icon-info-circled izenda-FilterSubscription-hintIcon u_inlineBlock"
                title={hintMessage}
              />}
          </GridLayout.Col>
        </GridLayout.Row>
      );
    });
  };

  /**
   * Render Component
   * @returns {object} component
   */
  render() {
    return (
      <GridLayout>
        {this.renderFilterSubscription()}
      </GridLayout>
    );
  }
}

export default FilterSubscription;



// WEBPACK FOOTER //
// ./ui/modules/Subscription/Izenda.FilterSubscription.js